/**
 * Google Analytics (gtag) Event Utility
 * A lightweight wrapper for sending custom gtag events
 */

(function (window) {
  'use strict';

  /**
   * Check if gtag is available and functional
   * @returns {boolean}
   */
  function isGtagAvailable() {
    return typeof window.gtag === 'function';
  }

  /**
   * Send a custom gtag event
   * @param {string} eventName - The name of the event (e.g., 'purchase', 'sign_up', 'button_click')
   * @param {Object} [params={}] - Event parameters
   * @param {Function} [callback] - Optional callback after event is sent
   * @returns {boolean} - Whether the event was sent successfully
   *
   * @example
   * // Basic event
   * GtagUtils.sendEvent('button_click');
   *
   * // Event with parameters
   * GtagUtils.sendEvent('purchase', {
   *   transaction_id: 'T12345',
   *   value: 99.99,
   *   currency: 'USD',
   *   items: [{ item_id: 'SKU123', item_name: 'Product Name' }]
   * });
   *
   * // Event with callback
   * GtagUtils.sendEvent('form_submit', { form_id: 'contact' }, function() {
   *   console.log('Event sent!');
   * });
   */
  function sendEvent(eventName, params, callback) {
    if (!eventName || typeof eventName !== 'string') {
      console.warn('[GtagUtils] Event name is required and must be a string');
      return false;
    }

    if (!isGtagAvailable()) {
      console.warn('[GtagUtils] gtag is not available');
      if (typeof callback === 'function') callback(false);
      return false;
    }

    var eventParams = params || {};

    // Add event callback if provided
    if (typeof callback === 'function') {
      eventParams.event_callback = function () {
        callback(true);
      };
    }

    try {
      window.gtag('event', eventName, eventParams);
      return true;
    } catch (e) {
      console.error('[GtagUtils] Error sending event:', e);
      if (typeof callback === 'function') callback(false);
      return false;
    }
  }

  /**
   * Track a page view
   * @param {string} [pagePath] - Optional page path (defaults to current path)
   * @param {string} [pageTitle] - Optional page title (defaults to document title)
   * @param {Object} [additionalParams={}] - Additional parameters
   * @returns {boolean}
   *
   * @example
   * // Track current page
   * GtagUtils.trackPageView();
   *
   * // Track specific page
   * GtagUtils.trackPageView('/products/custom-figure', 'Custom Figure Product');
   */
  function trackPageView(pagePath, pageTitle, additionalParams) {
    var params = Object.assign({}, additionalParams || {}, {
      page_path: pagePath || window.location.pathname,
      page_title: pageTitle || document.title,
      page_location: window.location.href
    });

    return sendEvent('page_view', params);
  }

  /**
   * Track an e-commerce event
   * @param {string} eventName - E-commerce event name (view_item, add_to_cart, purchase, etc.)
   * @param {Object} ecommerceData - E-commerce data object
   * @returns {boolean}
   */
  function trackEcommerce(eventName, ecommerceData) {
    var validEvents = [
      'view_item',
      'view_item_list',
      'select_item',
      'add_to_cart',
      'remove_from_cart',
      'view_cart',
      'begin_checkout',
      'add_shipping_info',
      'add_payment_info',
      'purchase',
      'refund'
    ];

    if (validEvents.indexOf(eventName) === -1) {
      console.warn('[GtagUtils] Unknown e-commerce event:', eventName);
    }

    return sendEvent(eventName, ecommerceData);
  }

  /**
   * Track a custom user interaction
   * @param {string} category - Event category (e.g., 'Button', 'Form', 'Video')
   * @param {string} action - Event action (e.g., 'click', 'submit', 'play')
   * @param {string} [label] - Event label for additional context
   * @param {number} [value] - Numeric value associated with the event
   * @returns {boolean}
   *
   * @example
   * // Track button click
   * GtagUtils.trackInteraction('Button', 'click', 'Upload Image');
   *
   * // Track with value
   * GtagUtils.trackInteraction('Form', 'submit', 'Contact Form', 1);
   */
  function trackInteraction(category, action, label, value) {
    var params = {
      event_category: category,
      event_action: action
    };

    if (label) params.event_label = label;
    if (typeof value === 'number') params.value = value;

    return sendEvent(action, params);
  }

  /**
   * Set user properties
   * @param {Object} properties - User properties to set
   * @returns {boolean}
   *
   * @example
   * GtagUtils.setUserProperties({
   *   user_type: 'premium',
   *   signup_date: '2024-01-15'
   * });
   */
  function setUserProperties(properties) {
    if (!isGtagAvailable()) {
      console.warn('[GtagUtils] gtag is not available');
      return false;
    }

    try {
      window.gtag('set', 'user_properties', properties);
      return true;
    } catch (e) {
      console.error('[GtagUtils] Error setting user properties:', e);
      return false;
    }
  }

  /**
   * Set the user ID for cross-device tracking
   * @param {string} userId - Unique user identifier
   * @returns {boolean}
   *
   * @example
   * GtagUtils.setUserId('user_12345');
   */
  function setUserId(userId) {
    if (!isGtagAvailable()) {
      console.warn('[GtagUtils] gtag is not available');
      return false;
    }

    try {
      window.gtag('set', { user_id: userId });
      return true;
    } catch (e) {
      console.error('[GtagUtils] Error setting user ID:', e);
      return false;
    }
  }

  // =====================================================
  // SnapFig-specific tracking methods
  // =====================================================

  /**
   * Get current product info from the page
   * @returns {Object} Product information
   */
  function getProductInfo() {
    var productForm = document.querySelector('form[id*="product-form"]');
    var variantInput = productForm ? productForm.querySelector('input[name="id"]') : null;
    var variantId = variantInput ? variantInput.value : '';

    // Extract product ID from form ID (format: product-form-{section_id}-{product_id})
    var productId = '';
    if (productForm) {
      var formId = productForm.getAttribute('id');
      if (formId && typeof formId === 'string') {
        var formIdParts = formId.split('-');
        // The product ID is the last part of the form ID
        if (formIdParts.length >= 4) {
          productId = formIdParts[formIdParts.length - 1];
        }
      }
    }

    // Fallback: try to get product handle from URL
    var productHandle = '';
    var path = window.location.pathname;
    if (path.includes('/products/')) {
      var match = path.match(/\/products\/([^\/\?]+)/);
      if (match) {
        productHandle = match[1];
      }
    }

    return {
      product_id: productId,
      product_handle: productHandle,
      variant_id: variantId
    };
  }

  /**
   * Get current page type based on first-level path segment
   * @returns {string} Page type (e.g., 'collections', 'products', 'cart', 'home')
   */
  function getPageType() {
    var path = window.location.pathname;

    // Root path (homepage)
    if (path === '/' || path === '') {
      return 'home';
    }

    // Extract the first-level path segment after the root /
    // e.g., /collections/best-seller → collections
    // e.g., /products/custom-figurine → products
    var match = path.match(/^\/([^\/\?]+)/);
    if (match && match[1]) {
      return match[1];
    }

    return 'home';
  }

  /**
   * Get entry source based on current page and referrer
   * - If current page is a collection: returns the collection name (last segment)
   * - If current page is a product: extracts collection name from referrer if available
   * @returns {string} Entry source value
   */
  function getEntrySource() {
    var currentPath = window.location.pathname;

    // Scenario 1: Current page is a collection page
    // Extract the last segment after /collections/
    if (currentPath.includes('/collections/')) {
      var collectionMatch = currentPath.match(/\/collections\/([^\/\?]+)/);
      if (collectionMatch && collectionMatch[1]) {
        return collectionMatch[1];
      }
    }

    // Scenario 2: Current page is a product page
    // Check if referrer contains /collections/ and extract the collection name
    if (currentPath.includes('/products/')) {
      if (document.referrer) {
        try {
          var referrerUrl = new URL(document.referrer);
          var referrerPath = referrerUrl.pathname;

          // Check if referrer contains /collections/
          if (referrerPath.includes('/collections/')) {
            var refCollectionMatch = referrerPath.match(/\/collections\/([^\/\?]+)/);
            if (refCollectionMatch && refCollectionMatch[1]) {
              return refCollectionMatch[1];
            }
          }
        } catch (e) {
          // Invalid referrer URL, fall through to default
        }
      }
    }

    // Default: return 'direct' if no valid entry source found
    return 'direct';
  }

  /**
   * Map sourceType to model_name for GA4 tracking
   * @param {string} sourceType - The API source type
   * @returns {string} The model name for GA4
   */
  function getModelName(sourceType) {
    var modelMapping = {
      'cute_single_img2img_deliver3d': 'seeddream',      // Quick Generate
      'cute_single_img2img_deliver3d_aws': 'chatgpt_4o'  // Better Generate
    };
    return modelMapping[sourceType] || 'chatgpt_4o'; // Default to chatgpt_4o
  }

  /**
   * Track upload click event
   * @param {Object} [params={}] - Additional parameters
   * @returns {boolean}
   */
  function trackUploadClick(params) {
    var productInfo = getProductInfo();
    var eventParams = Object.assign({
      page_type: getPageType(),
      entry_source: getEntrySource()
    }, productInfo, params || {});

    return sendEvent('upload_click', eventParams);
  }

  /**
   * Track upload confirm event
   * @param {string} fileName - Uploaded file name
   * @param {number} fileSize - File size in bytes
   * @param {Object} [params={}] - Additional parameters
   * @returns {boolean}
   */
  function trackUploadConfirm(fileName, fileSize, params) {
    var productInfo = getProductInfo();
    var eventParams = Object.assign({
      file_name: fileName || '',
      file_size: fileSize || 0
    }, productInfo, params || {});

    return sendEvent('upload_confirm', eventParams);
  }

  /**
   * Track upload preview close event
   * @param {string} fileName - File name that was being previewed
   * @param {Object} [params={}] - Additional parameters
   * @returns {boolean}
   */
  function trackUploadPreviewClose(fileName, params) {
    var productInfo = getProductInfo();
    var eventParams = Object.assign({
      file_name: fileName || ''
    }, productInfo, params || {});

    return sendEvent('upload_preview_close', eventParams);
  }

  /**
   * Track generate loading close event
   * @param {number} progressPercentage - Current progress percentage when closed
   * @param {Object} [params={}] - Additional parameters
   * @returns {boolean}
   */
  function trackGenerateLoadingClose(progressPercentage, params) {
    var eventParams = Object.assign({
      progress_percentage: progressPercentage || 0
    }, params || {});

    return sendEvent('generate_loading_close', eventParams);
  }

  /**
   * Track generation abandoned event (page unload during generation)
   * @param {number} stayDuration - Time spent on page in seconds
   * @param {number} imagesGenerated - Number of images generated in session
   * @param {string} modelName - Model name used for generation
   * @param {Object} [params={}] - Additional parameters
   * @returns {boolean}
   */
  function trackGenerationAbandoned(stayDuration, imagesGenerated, modelName, params) {
    var eventParams = Object.assign({
      stay_duration: stayDuration || 0,
      images_generated_in_session: imagesGenerated || 0,
      model_name: modelName || ''
    }, params || {});

    return sendEvent('generation_abandoned', eventParams);
  }

  /**
   * Track design confirm event
   * @param {string} imageId - Selected image ID
   * @param {Object} [params={}] - Additional parameters
   * @returns {boolean}
   */
  function trackDesignConfirm(imageId, params) {
    var productInfo = getProductInfo();
    var eventParams = Object.assign({
      image_id: imageId || ''
    }, productInfo, params || {});

    return sendEvent('design_confirm', eventParams);
  }

  /**
   * Track design regenerate click event
   * @param {string} modelName - Current model name
   * @param {number} previousImageCount - Number of images already generated
   * @param {Object} [params={}] - Additional parameters
   * @returns {boolean}
   */
  function trackDesignRegenerateClick(modelName = 'seeddream', previousImageCount, params) {
    var productInfo = getProductInfo();
    var eventParams = Object.assign({
      model_name: modelName || '',
      previous_image_count: previousImageCount || 0,
      entry_source: 'design_modal'
    }, productInfo, params || {});

    return sendEvent('design_regenerate_click', eventParams);
  }

  /**
   * Track design select close event (close without selection)
   * @param {number} generatedImageCount - Number of images that were generated
   * @param {Object} [params={}] - Additional parameters
   * @returns {boolean}
   */
  function trackDesignSelectClose(generatedImageCount, params) {
    var productInfo = getProductInfo();
    var eventParams = Object.assign({
      generated_image_count: generatedImageCount || 0
    }, productInfo, params || {});

    return sendEvent('design_select_close', eventParams);
  }

  // Expose the utility object
  window.GtagUtils = {
    // Core methods
    isAvailable: isGtagAvailable,
    sendEvent: sendEvent,
    trackPageView: trackPageView,
    trackEcommerce: trackEcommerce,
    trackInteraction: trackInteraction,
    setUserProperties: setUserProperties,
    setUserId: setUserId,

    // SnapFig-specific methods
    getProductInfo: getProductInfo,
    getPageType: getPageType,
    getEntrySource: getEntrySource,
    getModelName: getModelName,
    trackUploadClick: trackUploadClick,
    trackUploadConfirm: trackUploadConfirm,
    trackUploadPreviewClose: trackUploadPreviewClose,
    trackGenerateLoadingClose: trackGenerateLoadingClose,
    trackGenerationAbandoned: trackGenerationAbandoned,
    trackDesignConfirm: trackDesignConfirm,
    trackDesignRegenerateClick: trackDesignRegenerateClick,
    trackDesignSelectClose: trackDesignSelectClose
  };

})(window);
